import { useRef, useState, useEffect, FC, useCallback } from 'react';
import { isMobile } from 'react-device-detect';
import {
  StyledInlineMarkdown,
  StyledInlineMarkdownContainer,
  StyledInlineMarkdownWrapper,
  StyledOverlayTrigger,
} from '../styles';
import { KizenTypography } from '__app/typography';
import { useTooltip } from '__components/Kizen/Tooltip';
import { useSanitizedHtml } from '__hooks/useSanitizedHtml';
import { MarkdownEditor, type MarkdownHandle } from '..';
import { MarkdownOverlay } from './MarkdownOverlay';
import { usePropCallback } from '__components/WYSIWYG/editor';
import { getHTMLValue, getRawTextValue } from '../helpers';
import { PREVIEW_LENGTH, PREVIEW_LENGTH_OFFSET } from '../constants';
import { applyRef } from '__components/Inputs/props';

export const InlineMarkdownText: FC<{
  isMarkdown?: boolean;
  value: string;
  displayValue?: string;
  isOpen: boolean;
  onSave?: (value: string) => void;
  onOpen: () => void;
  onHide: (value?: string) => void;
  readonly?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  editorRef?: React.MutableRefObject<MarkdownHandle | null>;
  inModal?: boolean;
  fullTooltip?: boolean;
}> = ({
  isMarkdown = true,
  value,
  displayValue,
  isOpen,
  onSave,
  onOpen,
  onHide,
  readonly,
  onFocus,
  onBlur,
  editorRef,
  inModal = false,
  fullTooltip = false,
}) => {
  const [rawHtml, setHtml] = useState<string>('');
  const [rawFullHtml, setFullHtml] = useState<string>('');

  const html = useSanitizedHtml(rawHtml) as string;
  const fullHtml = useSanitizedHtml(rawFullHtml) as string;
  const sanitizedDisplayValue = useSanitizedHtml(displayValue ?? '') as string;

  useEffect(() => {
    if (!fullTooltip) return;
    const convertAction = async () => {
      setFullHtml(
        value ? await getHTMLValue(value, isMarkdown, { condensed: true }) : ''
      );
    };
    convertAction();
  }, [value, isMarkdown, fullTooltip]);

  useEffect(() => {
    const convertAction = async () => {
      const html = value
        ? await getHTMLValue(
            value.slice(0, PREVIEW_LENGTH + PREVIEW_LENGTH_OFFSET),
            isMarkdown,
            {
              truncate: { characterLimit: PREVIEW_LENGTH, showEllipsis: true },
              condensed: true,
            }
          )
        : '';
      setHtml(html);
    };
    convertAction();
  }, [value, isMarkdown]);

  const [tooltipProps, tooltip] = useTooltip(
    {
      html: fullTooltip ? fullHtml : html,
      maxWidth: 500,
    },
    () => !!value?.trim() && !isMobile && !isOpen
  );

  const contentRef = useRef<HTMLParagraphElement | null>(null);
  const targetRef = useRef<HTMLDivElement | null>(null);
  const editorInnerRef = useRef<MarkdownHandle | null>(null);

  const mergeRef = useCallback(
    (markdownHandle: MarkdownHandle | null) => {
      applyRef(editorInnerRef, markdownHandle);
      applyRef(editorRef, markdownHandle);
    },
    [editorRef]
  );

  const [overflow, setOverflow] = useState(false);

  useEffect(() => {
    const offset = html.match(/…<\/\s*\w+>$/) ? 11 : 3;
    const observer = new ResizeObserver(() => {
      if (contentRef.current) {
        setOverflow(
          contentRef.current.offsetWidth + offset <
            contentRef.current.scrollWidth
        );
      }
    });
    if (contentRef.current) {
      observer.observe(contentRef.current);
      setOverflow(
        contentRef.current.offsetWidth + offset < contentRef.current.scrollWidth
      );
    }
    return () => {
      observer.disconnect();
    };
  }, [html]);

  const handleOpen = () => {
    if (!isOpen && (!readonly || value?.trim())) {
      onOpen();
    }
  };

  const onHideCB = usePropCallback(onHide);

  const handleHide = useCallback(async () => {
    const editor = editorInnerRef.current?.editor;
    if (editor && !readonly) {
      const value = await getRawTextValue(editor, isMarkdown);
      onHideCB(isMarkdown ? value.trim() : value);
    } else {
      onHideCB(null);
    }
  }, [onHideCB, readonly, isMarkdown]);

  const handleSave = (value: string) => {
    if (!readonly) {
      onSave?.(value.trim());
    }
  };

  return (
    <StyledInlineMarkdownContainer onClick={handleOpen} disabled={readonly}>
      <StyledOverlayTrigger ref={targetRef} />
      {tooltip}
      <StyledInlineMarkdownWrapper>
        <StyledInlineMarkdown
          ref={contentRef}
          dangerouslySetInnerHTML={{
            __html: sanitizedDisplayValue || html || '—',
          }}
          {...tooltipProps}
          data-qa="inline-markdown-text"
        />
        {overflow && <KizenTypography>…</KizenTypography>}
      </StyledInlineMarkdownWrapper>
      <MarkdownOverlay
        show={isOpen}
        onHide={handleHide}
        targetRef={targetRef}
        containerRef={contentRef}
        inModal={inModal}
      >
        <MarkdownEditor
          isMarkdown={isMarkdown}
          initialValue={value}
          autofocus="end"
          onSave={handleSave}
          editorRef={mergeRef}
          readonly={readonly}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      </MarkdownOverlay>
    </StyledInlineMarkdownContainer>
  );
};
